// SearchableFavoriteSelect — dropdown buscable con miniaturas para el signup.
//
// Se usa en /signup cuando el partner exige tipos de favorito en la creacion
// de cuenta (ver SignupConfig). Por cada tipo, el form renderiza una
// instancia que:
//   1. Al abrir NO carga nada: el campo arranca vacio con una lupa, invitando
//      a buscar. Recien dispara la consulta cuando el usuario tipeo MIN_QUERY
//      caracteres o mas (evita traer un listado gigante de equipos por default).
//   2. Al tipear (>= MIN_QUERY chars), debounce 250ms y fetch con `q=` para
//      usar FTS+trigram. Mientras consulta, muestra un spinner en el input para
//      que el usuario sepa que el resultado todavia no llego.
//   3. Siempre incluye una opcion "Otro" al final (value: null) — el user
//      DEBE elegir algo para poder submitear el form pero "Otro" cuenta
//      como eleccion valida (se puede elegir sin necesidad de buscar).
//
// Proyeccion de campos: el config publico declara `title_field` (string,
// "name" o "details.<key>") y `image_field` (idem, opcional). Resolvemos los
// paths client-side contra cada item.
//
// Accesibilidad: combobox pattern (input + listbox), aria-activedescendant.
// Keyboard: arrow up/down, enter para seleccionar, escape para cerrar.

import { useEffect, useId, useRef, useState } from 'react';
import { ApiError, fetchFavoriteItems, type PublicFavoriteItem } from '@/lib/api';
import type { FieldStatus } from '@/components/FormField';

const DEBOUNCE_MS = 250;
/**
 * Minimo de caracteres antes de disparar la busqueda. Por debajo de esto el
 * campo no consulta el backend (no hay carga inicial de equipos por default).
 */
const MIN_QUERY_LENGTH = 3;
/**
 * Sentinel value para "Otro". Se convierte a null al notificar al parent.
 * Usar un string permite manejar el value del combobox sin ramas especiales
 * (los selectores DOM no admiten value=null).
 */
const NONE_VALUE = '__none__';

export interface SelectedFavorite {
  /** ID del item elegido. `null` si el usuario selecciono "Otro". */
  favorite_id: string | null;
  /** Display string mostrado en el input cuando hay seleccion. */
  display: string;
}

export interface SearchableFavoriteSelectProps {
  partnerId: string;
  favoriteTypeId: string;
  /** Label encima del input. */
  label: string;
  /** Path al campo titulo en el item: "name" o "details.<key>". */
  titleField: string;
  /** Path al campo imagen URL: "details.<key>". Opcional. */
  imageField: string | null;
  /** Cantidad de items a mostrar antes de que el usuario tipee. */
  initialLimit: number;
  /** Texto del fallback "Otro" — i18n del partner si se quiere. */
  noneLabel?: string;
  value: SelectedFavorite | null;
  onChange: (value: SelectedFavorite | null) => void;
  disabled?: boolean;
  required?: boolean;
  /** Contorno de estado envolvente (mismo sistema que FormField). */
  status?: FieldStatus;
}

export default function SearchableFavoriteSelect({
  partnerId,
  favoriteTypeId,
  label,
  titleField,
  imageField,
  initialLimit,
  noneLabel = 'Otro',
  value,
  onChange,
  disabled = false,
  required = true,
  status = 'idle',
}: SearchableFavoriteSelectProps): JSX.Element {
  const id = useId();
  const listboxId = `${id}-listbox`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<PublicFavoriteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch items: SOLO cuando el usuario tipeo MIN_QUERY_LENGTH+ caracteres.
  // Sin carga inicial — por debajo del minimo limpiamos resultados y no
  // tocamos el backend (el dropdown muestra el hint "escribi N caracteres").
  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setLoading(false);
      setErr(null);
      return;
    }
    // Feedback inmediato: marcamos loading apenas el query es valido, antes del
    // debounce, para que el spinner aparezca sin esperar a que dispare el fetch.
    setLoading(true);
    setErr(null);
    const handle = setTimeout(() => {
      fetchFavoriteItems(partnerId, {
        favorite_type_id: favoriteTypeId,
        q: trimmed,
        limit: initialLimit,
      })
        .then((res) => {
          setItems(res.items);
          setHighlight(0);
        })
        .catch((e: unknown) => {
          const msg = e instanceof ApiError ? e.message : 'Error cargando items.';
          setErr(msg);
          setItems([]);
        })
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [open, query, partnerId, favoriteTypeId, initialLimit]);

  // Click fuera → cerrar.
  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Opciones renderizables: items + "Otro" pinned al final.
  // Cada uno con un value estable para el listbox y la navegacion teclado.
  type Option =
    | { kind: 'item'; value: string; item: PublicFavoriteItem; title: string; image: string | null }
    | { kind: 'none'; value: typeof NONE_VALUE };

  const options: Option[] = [
    ...items.map<Option>((item) => ({
      kind: 'item',
      value: item.favorite_id,
      item,
      title: projectFieldDisplay(item, titleField) ?? `(${item.code})`,
      image: imageField ? projectFieldDisplay(item, imageField) : null,
    })),
    { kind: 'none', value: NONE_VALUE },
  ];

  function selectOption(opt: Option): void {
    if (opt.kind === 'none') {
      onChange({ favorite_id: null, display: noneLabel });
    } else {
      onChange({ favorite_id: opt.value, display: opt.title });
    }
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlight];
      if (opt) selectOption(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  const inputValue = open ? query : (value?.display ?? '');

  return (
    <div className="field space-y-1" data-status={status} ref={containerRef}>
      <label htmlFor={id} className="block text-sm font-medium text-text">
        {label}
        {required ? <span className="text-error ml-0.5" aria-hidden>*</span> : null}
      </label>
      <div className="relative">
        {/* Lupa fija a la izquierda: ancla visual de "esto es un buscador". */}
        <span
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          aria-hidden
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-autocomplete="list"
          aria-activedescendant={
            open && options[highlight] ? `${id}-opt-${highlight}` : undefined
          }
          type="text"
          autoComplete="off"
          value={inputValue}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
            // Cambio de query implica deseleccion (el display ya no matchea).
            if (value !== null) onChange(null);
          }}
          onKeyDown={onKeyDown}
          placeholder={`Buscar ${label.toLowerCase()}…`}
          disabled={disabled}
          required={required}
          className="w-full min-h-[2.75rem] rounded-lg border border-text-muted/30 bg-surface pl-9 pr-9 py-2 text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
        />
        {/* Spinner mientras consulta: el usuario sabe que el resultado aun no llego. */}
        {loading ? (
          <span
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-primary"
            role="status"
            aria-label="Buscando"
          >
            <svg
              className="animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="3"
                opacity="0.25"
              />
              <path
                d="M21 12a9 9 0 0 0-9-9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </span>
        ) : null}
        {open ? (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-10 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-text-muted/30 bg-surface shadow-lg"
          >
            {!loading && query.trim().length < MIN_QUERY_LENGTH ? (
              <li className="px-3 py-2 text-sm text-text-muted">
                Escribí al menos {MIN_QUERY_LENGTH} caracteres para buscar.
              </li>
            ) : null}
            {loading ? (
              <li className="px-3 py-2 text-sm text-text-muted">Buscando…</li>
            ) : null}
            {err ? (
              <li className="px-3 py-2 text-sm text-error">{err}</li>
            ) : null}
            {!loading && !err && items.length === 0 && query.trim().length >= MIN_QUERY_LENGTH ? (
              <li className="px-3 py-2 text-sm text-text-muted">
                Sin resultados. Probá con otro término.
              </li>
            ) : null}
            {options.map((opt, idx) => {
              const isHighlighted = idx === highlight;
              const isSelected =
                opt.kind === 'item'
                  ? value?.favorite_id === opt.value
                  : value?.favorite_id === null;
              return (
                <li
                  key={opt.kind === 'item' ? opt.value : NONE_VALUE}
                  id={`${id}-opt-${idx}`}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => {
                    // mousedown (antes de blur) para que el click no cierre el
                    // dropdown via onClickOutside antes de procesar la seleccion.
                    e.preventDefault();
                    selectOption(opt);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm border-t border-text-muted/10 first:border-t-0 ${
                    isHighlighted ? 'bg-primary/10' : ''
                  } ${opt.kind === 'none' ? 'text-text-muted italic' : 'text-text'}`}
                >
                  {opt.kind === 'item' ? (
                    <>
                      {opt.image ? (
                        <img
                          src={opt.image}
                          alt=""
                          className="w-6 h-6 rounded object-cover flex-shrink-0"
                          onError={(e) => {
                            e.currentTarget.style.visibility = 'hidden';
                          }}
                        />
                      ) : (
                        <span
                          className="w-6 h-6 rounded bg-text-muted/20 flex-shrink-0"
                          aria-hidden
                        />
                      )}
                      <span className="truncate">{opt.title}</span>
                    </>
                  ) : (
                    <>
                      <span
                        className="w-6 h-6 rounded bg-text-muted/10 flex-shrink-0"
                        aria-hidden
                      />
                      <span>{noneLabel}</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Resuelve `title_field` / `image_field` (formato "name" o "details.<key>")
 * sobre un item publico. Devuelve string si el valor es string/number/bool,
 * sino null.
 */
function projectFieldDisplay(
  item: { name: string; details: Record<string, unknown> },
  ref: string,
): string | null {
  let value: unknown;
  if (ref === 'name') {
    value = item.name;
  } else if (ref.startsWith('details.')) {
    value = item.details?.[ref.slice('details.'.length)];
  } else {
    return null;
  }
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}
